/**
 * Ink for an arbitrary fill — the one luminance rule for text drawn on a
 * colour the brand does not tokenise an ink for (team colours, chart
 * fills). Accepts a hex or a `var(--token, #fallback)` expression; the
 * token is resolved from the document (the tenant's tokens live on
 * `:root`), falling back to the literal fallback, then to white.
 *
 * `--text-inverse` is NOT this: it is the ink on `--accent-primary`
 * (dark on Bet247's yellow). Reading it as "white" is what put navy names
 * on navy cards (2026-09-08).
 */
export function inkOnHex(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/.exec(hex.trim());
  if (!m || m[1] === undefined) return '#FFFFFF';
  const n = parseInt(m[1], 16);
  const lum =
    0.2126 * ((n >> 16) & 0xff) + 0.7152 * ((n >> 8) & 0xff) + 0.0722 * (n & 0xff);
  return lum > 150 ? '#0B0E17' : '#FFFFFF';
}

const VAR_RE = /^var\(\s*(--[A-Za-z0-9-]+)\s*(?:,\s*(.+))?\)$/;

/** Resolve a CSS colour expression to a hex where possible. */
export function resolveFill(fill: string): string | null {
  const v = fill.trim();
  if (/^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/.test(v)) return v;
  const m = VAR_RE.exec(v);
  if (!m || m[1] === undefined) return null;
  if (typeof document !== 'undefined') {
    const fromRoot = getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim();
    if (fromRoot) return resolveFill(fromRoot);
  }
  return m[2] !== undefined ? resolveFill(m[2]) : null;
}

/** Ink for a fill expression; white when the fill cannot be resolved. */
export function inkOn(fill: string): string {
  const hex = resolveFill(fill);
  return hex === null ? '#FFFFFF' : inkOnHex(hex);
}
